import type { ConsentInfo } from '#definitions/consent-info';

/** The parts of `/consent/info` that map a site back to its organization. */
export type OrgMatchInfo = Pick<ConsentInfo, 'availableOrganizations'>;

/** Site URL -> owning orgId, as built by `buildSiteOrgIndex`. */
export type SiteOrgIndex = ReadonlyMap<string, string>;

/**
 * Compare site URLs on their bare host.
 *
 * `availableWorkspaces` and `availableSites[].url` are bare hosts, while
 * `units[].workspaces[].workspaceUrl` carries a scheme, so the two sources only
 * line up once the scheme and any trailing slash are stripped.
 */
function normalizeSiteUrl(rawUrl: string | null | undefined): string | null {
  const url = rawUrl
    ?.trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');
  return url ? url.toLowerCase() : null;
}

/**
 * Index every site URL `/consent/info` attributes to an organization.
 *
 * `availableWorkspaces` is the authoritative per-org site list, so it is read
 * first and wins; `units[].workspaces[]` is read as a fallback for responses
 * that populate the units tree but not the flat list. A URL already indexed is
 * never overwritten, so the first org to claim a site keeps it.
 */
export function buildSiteOrgIndex(info: OrgMatchInfo): SiteOrgIndex {
  const index = new Map<string, string>();

  const claim = (rawUrl: string | null | undefined, orgId: string) => {
    const url = normalizeSiteUrl(rawUrl);
    if (url && !index.has(url)) {
      index.set(url, orgId);
    }
  };

  for (const org of info.availableOrganizations ?? []) {
    if (!org?.orgId) continue;
    for (const workspace of org.availableWorkspaces ?? []) {
      claim(workspace, org.orgId);
    }
  }

  for (const org of info.availableOrganizations ?? []) {
    if (!org?.orgId) continue;
    for (const unit of org.units ?? []) {
      for (const workspace of unit.workspaces ?? []) {
        claim(workspace.workspaceUrl, org.orgId);
        claim(workspace.cloudUrl, org.orgId);
      }
    }
  }

  return index;
}

/**
 * The organization owning a selected site, or null when the response does not
 * say. Null means "unknown", which callers treat as "no conflict" rather than
 * as a mismatch — see `isOrgMismatch`.
 */
export function resolveSiteOrgId(
  index: SiteOrgIndex,
  siteUrl: string | null | undefined,
): string | null {
  const url = normalizeSiteUrl(siteUrl);
  return (url && index.get(url)) || null;
}

/**
 * Whether the site and Bitbucket selections are in different organizations.
 *
 * A consent grants one organization's data, so the two cannot be granted
 * together. Only compared when both are known: a missing selection, or a site
 * we cannot attribute to an org, fails open rather than blocking a combination
 * we cannot actually judge.
 */
export function isOrgMismatch(
  siteOrgId: string | null,
  bitbucketOrgId: string | null,
): boolean {
  return (
    siteOrgId != null && bitbucketOrgId != null && siteOrgId !== bitbucketOrgId
  );
}

/** Which org helper a picker shows below its field. */
export type OrgHelpKind = 'none' | 'constraint' | 'empty';

/**
 * How the other field's selection constrains this one: which options to
 * disable, and which helper to explain it with. Shared by the site and
 * Bitbucket workspace pickers, which apply the same rule in both directions.
 *
 * `constraint` says only same-org options are selectable, so it is only worth
 * showing while something is still pickable — once every option is other-org
 * that line is a dead end, and `empty` says none are available instead.
 * Unknown orgs fail open, exactly as `isOrgMismatch` does.
 */
export function getOrgOptionState<TItem>({
  items,
  otherOrgId,
  getOrgId,
  canShowHelp,
}: {
  items: readonly TItem[];
  /** Org owning the other field's selection, or null when none/unknown. */
  otherOrgId: string | null;
  getOrgId: (item: TItem) => string | null;
  /** False where a higher-priority message already owns the slot. */
  canShowHelp: boolean;
}): {
  /** Hand to MultiSelect's `isItemDisabled`; undefined leaves every option enabled. */
  isItemDisabled?: (item: TItem) => boolean;
  helpKind: OrgHelpKind;
} {
  if (!otherOrgId) {
    return { helpKind: 'none' };
  }
  const isItemDisabled = (item: TItem): boolean =>
    isOrgMismatch(getOrgId(item), otherOrgId);
  const disabledCount = items.filter(isItemDisabled).length;
  return {
    isItemDisabled,
    helpKind:
      !canShowHelp || disabledCount === 0
        ? 'none'
        : disabledCount === items.length
          ? 'empty'
          : 'constraint',
  };
}
